/* =========================================================================
 * THE POWER FOUR — reconciled against each conference's OWN sponsored-sport
 * list, 2025-26.
 *
 * Brian: "Yes, conference regular title and tournament title get recorded."
 *
 * ⚠️ THE RECONCILIATION FOUND PHANTOMS BEFORE IT FOUND GAPS, and that is the
 * headline. We track ELEVEN Power Four championships that do not exist —
 * a Big Ten men's volleyball title (the Big Ten does not sponsor men's
 * volleyball; those programs play in the EIVA/MIVA), Big Ten and ACC water polo
 * (MPSF/CWPA sports, not conference ones), SEC men's soccer and SEC lacrosse
 * (not sponsored at all), Big 12 field hockey and Big 12 men's lacrosse.
 *
 * A phantom is worse than a gap: a rep works it, waits for a winner who is
 * never crowned, and eventually stops trusting the board. They are listed in
 * `P4_PHANTOMS` below and are NOT retired from here — retiring is a write
 * against a live record and belongs behind a human, the same way the 20 earlier
 * phantoms were renamed rather than deleted.
 *
 * ⚠️ ONLY VERIFIED TITLES ARE CREATED. Where a conference's own site did not
 * state whether a regular-season champion is recognized — Big Ten soccer,
 * tennis, lacrosse, field hockey and ice hockey all came back "not established
 * from source" — NOTHING is recorded. That is the whole discipline of
 * `conferenceTitles.js` applied: a league sport does not automatically get a
 * pair, and the absence of a source is not evidence of absence OR of presence.
 *
 * ⚠️ THE AMBIGUOUS RECORD WE ALREADY HOLD IS LEFT ALONE. For each pair below we
 * create only the REGULAR-SEASON half, because the existing record
 * ("Big Ten Conference Men's Basketball Championship") is almost certainly the
 * tournament — but "almost certainly" is a guess, and relabelling it would
 * attach real champions to the wrong title. It stays flagged by the
 * `confTitleAmbiguous` audit defect until a human confirms it. Additive now,
 * correct later; never a silent relabel.
 * ========================================================================= */

const SRC = {
  b1g: 'https://bigten.org/article/blt5764f8c9035fea5a/',
  sec: 'https://www.secsports.com/news/2024/08/rowing-becomes-22nd-conference-sports-to-be-sponsored-by-sec',
  acc: 'https://theacc.com/',
  b12: 'https://big12sports.com/',
}

/**
 * A conference championship record.
 *
 * `segment: 'Conference'` AND `level: 'Conference'` are both set because the
 * object is inconsistent about which one carries it — 2,722 records use the
 * level and 852 the segment. Setting both is how a new record is found by
 * either filter instead of depending on which one a given view happens to read.
 */
const C = (code, conference, sport, gender, title, source, opts = {}) => ({
  /* ⚠️ `st` IS THE KEY NAMESPACE, NOT A STATE. `toItems` builds the key as
   * SR-{st}-{sport}-{division}-{gender} and `sr_state` comes from `state`, so
   * `st` never reaches a HubSpot field. Passing 'US' for every conference is
   * what made all four Power Four conferences collapse onto ONE key per sport
   * — "SR-US-gymnastics-championship-women" was the Big Ten's, the SEC's, the
   * ACC's and the Big 12's gymnastics title all at once, nine collisions in
   * total. The conference code is what makes the key say which conference. */
  st: code, state: '', body: conference, sport, gender,
  divisions: [title],
  source,
  segment: 'Conference', level: 'Conference',
  season: opts.season || '2026',
  activity: false,
  note: opts.note || '',
})

/* ---- THE MISSING SPORTS ------------------------------------------------
 * Sports each conference genuinely sponsors and we hold no record for. These
 * are unambiguous single titles or clearly-named events — no pairing question
 * arises, so they are the safest half of the acquisition. */
const MISSING_SPORTS = [
  /* The Big Ten's men's ice hockey TOURNAMENT is new for March 2026 — a
   * two-week single-elimination bracket. Recorded as the tournament it is;
   * whether a separate regular-season champion is recognized was NOT
   * established from the conference's own site, so it is not recorded. */
  C('B1G', 'Big Ten Conference', 'Ice Hockey', 'Men', 'Championship', SRC.b1g,
    { note: 'New two-week single-elimination bracket beginning March 2026.' }),
  C('B1G', 'Big Ten Conference', 'Gymnastics', 'Men', 'Championship', SRC.b1g,
    { note: 'One of the few Division I men\'s gymnastics conference championships.' }),
  C('B1G', 'Big Ten Conference', 'Gymnastics', 'Women', 'Championship', SRC.b1g),

  C('SEC', 'Southeastern Conference', 'Gymnastics', 'Women', 'Championship', SRC.sec,
    { note: 'The SEC does not sponsor men\'s gymnastics.' }),
  C('SEC', 'Southeastern Conference', 'Equestrian', 'Women', 'Championship', SRC.sec,
    { note: 'Scored by discipline (dressage / hunt seat / jumping / western).' }),
  C('SEC', 'Southeastern Conference', 'Rowing', 'Women', 'Championship', SRC.sec,
    { note: 'Added 2024 as the SEC\'s 22nd sponsored sport and 13th women\'s sport.' }),

  C('ACC', 'Atlantic Coast Conference', 'Fencing', 'Men', 'Championship', SRC.acc),
  C('ACC', 'Atlantic Coast Conference', 'Fencing', 'Women', 'Championship', SRC.acc),
  C('ACC', 'Atlantic Coast Conference', 'Gymnastics', 'Women', 'Championship', SRC.acc),

  C('B12', 'Big 12 Conference', 'Equestrian', 'Women', 'Championship', SRC.b12),
  C('B12', 'Big 12 Conference', 'Beach Volleyball', 'Women', 'Championship', SRC.b12),
  C('B12', 'Big 12 Conference', 'Gymnastics', 'Women', 'Championship', SRC.b12),
]

/* ---- THE VERIFIED REGULAR-SEASON TITLES --------------------------------
 * ONLY where the conference's own site confirms a regular-season champion is
 * recognized separately from the tournament champion.
 *
 * ⚠️ THE ACC AND BIG 12 ARE ABSENT FROM THIS BLOCK ON PURPOSE. Their sponsored
 * -sport lists were verified; their per-sport regular-season/tournament split
 * was not. Creating their pairs "because the Big Ten and SEC do it that way"
 * would be exactly the inference this whole model exists to refuse — the
 * NESCAC and the pre-2017 Ivy League are the standing proof that the default
 * is not safe. */
const REGULAR_SEASON = [
  C('B1G', 'Big Ten Conference', 'Basketball', 'Men', 'Regular-Season Championship', SRC.b1g),
  C('B1G', 'Big Ten Conference', 'Basketball', 'Women', 'Regular-Season Championship', SRC.b1g),
  C('B1G', 'Big Ten Conference', 'Baseball', 'Men', 'Regular-Season Championship', SRC.b1g,
    { note: 'Co-champions are possible — the title is decided on conference record.' }),
  C('B1G', 'Big Ten Conference', 'Softball', 'Women', 'Regular-Season Championship', SRC.b1g),
  /* ⚠️ BIG TEN VOLLEYBALL HAS NO TOURNAMENT YET. The inaugural Big Ten
   * Volleyball Tournament is November 2026 — the 2026-27 academic year. For
   * 2025-26 the conference crowns a regular-season champion ONLY, so a
   * tournament record dated now would be a phantom. The same trap as Ivy
   * League basketball before 2017. */
  C('B1G', 'Big Ten Conference', 'Volleyball', 'Women', 'Regular-Season Championship', SRC.b1g,
    { note: 'Regular-season title only through 2025-26; the inaugural Big Ten Volleyball Tournament is November 2026.' }),

  C('SEC', 'Southeastern Conference', 'Basketball', 'Men', 'Regular-Season Championship', SRC.sec),
  C('SEC', 'Southeastern Conference', 'Basketball', 'Women', 'Regular-Season Championship', SRC.sec),
  /* The SEC is explicit about this one: the baseball CONFERENCE championship is
   * determined solely by regular-season record, separate from the tournament. */
  C('SEC', 'Southeastern Conference', 'Baseball', 'Men', 'Regular-Season Championship', SRC.sec,
    { note: 'The SEC baseball conference championship is decided solely by regular-season record.' }),
  C('SEC', 'Southeastern Conference', 'Softball', 'Women', 'Regular-Season Championship', SRC.sec),
  C('SEC', 'Southeastern Conference', 'Soccer', 'Women', 'Regular-Season Championship', SRC.sec,
    { note: 'The regular-season champion hosts the tournament semifinal and final. The SEC does not sponsor men\'s soccer.' }),
  C('SEC', 'Southeastern Conference', 'Tennis', 'Men', 'Regular-Season Championship', SRC.sec,
    { note: 'Best winning percentage in conference regular-season dual matches.' }),
  C('SEC', 'Southeastern Conference', 'Tennis', 'Women', 'Regular-Season Championship', SRC.sec),
  C('SEC', 'Southeastern Conference', 'Volleyball', 'Women', 'Regular-Season Championship', SRC.sec,
    { note: 'The SEC volleyball tournament was revived in 2025 after a 20-year gap.' }),
]

/* =========================================================================
 * ⚠️ PHANTOMS WE CURRENTLY HOLD — recorded here, NOT retired from here.
 *
 * Each is a championship on the object for a sport the conference does not
 * sponsor. Retiring a live record is a write with real consequences (a rep may
 * already have a pursuit against it), so this list is EVIDENCE for a human
 * decision, presented with the reason and the source — the same treatment the
 * earlier 20 phantoms got, where the resolution was a rename to "RETIRED - "
 * and never a delete.
 * ========================================================================= */
export const P4_PHANTOMS = [
  { conference: 'Big Ten Conference', sport: 'Volleyball', gender: 'Men',
    reason: 'The Big Ten does not sponsor men\'s volleyball; those programs compete in the EIVA/MIVA.', source: SRC.b1g },
  { conference: 'Big Ten Conference', sport: 'Water Polo', gender: 'Men',
    reason: 'The Big Ten does not sponsor water polo; it is an MPSF/CWPA sport.', source: SRC.b1g },
  { conference: 'Big Ten Conference', sport: 'Water Polo', gender: 'Women',
    reason: 'The Big Ten does not sponsor water polo; it is an MPSF/CWPA sport.', source: SRC.b1g },

  { conference: 'Southeastern Conference', sport: 'Soccer', gender: 'Men',
    reason: 'The SEC sponsors women\'s soccer only.', source: SRC.sec },
  { conference: 'Southeastern Conference', sport: 'Lacrosse', gender: 'Men',
    reason: 'The SEC does not sponsor lacrosse.', source: SRC.sec },
  { conference: 'Southeastern Conference', sport: 'Lacrosse', gender: 'Women',
    reason: 'The SEC does not sponsor lacrosse.', source: SRC.sec },

  { conference: 'Atlantic Coast Conference', sport: 'Volleyball', gender: 'Men',
    reason: 'The ACC does not sponsor men\'s volleyball.', source: SRC.acc },
  { conference: 'Atlantic Coast Conference', sport: 'Water Polo', gender: 'Men',
    reason: 'The ACC does not sponsor water polo.', source: SRC.acc },
  { conference: 'Atlantic Coast Conference', sport: 'Water Polo', gender: 'Women',
    reason: 'The ACC does not sponsor water polo.', source: SRC.acc },

  { conference: 'Big 12 Conference', sport: 'Field Hockey', gender: 'Women',
    reason: 'The Big 12 does not sponsor field hockey.', source: SRC.b12 },
  { conference: 'Big 12 Conference', sport: 'Lacrosse', gender: 'Men',
    reason: 'The Big 12 sponsors women\'s lacrosse only.', source: SRC.b12 },
]

export const GAPS_P4 = [...MISSING_SPORTS, ...REGULAR_SEASON]
